// Planera jakt (J1): datamodellen för planeraren som rena funktioner —
// utkast, validering per steg, lottning av pass, mottagare och det som skickas till API:t.
import type { CalendarEventInvite, MarkCalendarEvent, PlanHuntInput } from '../../api';

export type MarkerType = 'pass' | 'hund_start' | 'samlingsplats' | 'lunch' | 'fika' | 'viltparad';

export const MARKER_LABEL: Record<MarkerType, string> = {
  pass: 'Pass', hund_start: 'Hundförare', samlingsplats: 'Samlingsplats', lunch: 'Lunch', fika: 'Fika', viltparad: 'Viltparad',
};

export interface PlanMarker {
  id: string;
  type: MarkerType;
  lat: number;
  lng: number;
  label: string;
  /** Mottagaren som fått passet: `m:<medlems-id>` eller `g:<e-post/telefon>`. */
  assigneeKey?: string | null;
  /** Namnet på den som fått passet (för äldre vyer som läser satPlanJson). */
  assignedName?: string;
  /** Låst pass (t.ex. jaktledare/hundförare): lottningen rör det inte. */
  locked?: boolean;
}

/** markSatId = markens såt (S1, sub_area i markgränsen) som planens såt gäller; saknas = egen såt för dagen. */
export interface PlanSat { id: string; namn: string; startTid?: string; slutTid?: string; markers: PlanMarker[]; markSatId?: string | null }

export interface Guest { key: string; name: string; email?: string; phone?: string }

export interface PlanDraft {
  version: 2;
  title: string;
  /** YYYY-MM-DD */
  date: string;
  /** HH:MM */
  meetingTime: string;
  startTime: string;
  endTime: string;
  meetingPlace: string;
  meetingLat: number | null;
  meetingLng: number | null;
  satar: PlanSat[];
  memberIds: string[];
  guests: Guest[];
  message: string;
  utrustning: string[];
  contactName: string;
  contactPhone: string;
  sendEmail: boolean;
}

export interface PlanMember { id: string; name: string; userEmail?: string | null; phone?: string | null; dogHandler?: boolean; role?: string }

export const UTRUSTNING = [
  'Signalfärgad väst', 'Signalfärgad mössa', 'Jaktradio', 'Kompass', 'GPS', 'Kikare', 'Avståndsmätare',
  'Skjutstöd', 'Skjutstol', 'Förstahjälpen', 'Kniv', 'Draglina', 'Slaktväska', 'Termos', 'Egen mat', 'Hundväska',
];

export const STEPS = [
  { key: 'nar', label: 'När' },
  { key: 'plats', label: 'Plats' },
  { key: 'vilka', label: 'Vilka' },
  { key: 'pass', label: 'Pass' },
  { key: 'skicka', label: 'Skicka' },
] as const;
export type StepKey = typeof STEPS[number]['key'];

const pad = (n: number) => String(n).padStart(2, '0');
export const isoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const hhmm = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
export const uid = () => Math.random().toString(36).slice(2, 10);

/** Nästa lördag (eller idag om det är lördag). */
export function nextSaturday(now = new Date()): string {
  const d = new Date(now);
  d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7));
  return isoDate(d);
}

export function emptyDraft(opts: { markName: string; date?: string; me?: PlanMember | null; userName?: string | null }): PlanDraft {
  return {
    version: 2,
    title: `Jakt – ${opts.markName}`,
    date: opts.date || nextSaturday(),
    meetingTime: '06:30',
    startTime: '07:30',
    endTime: '15:00',
    meetingPlace: '',
    meetingLat: null,
    meetingLng: null,
    satar: [],
    memberIds: opts.me ? [opts.me.id] : [],
    guests: [],
    message: '',
    utrustning: ['Signalfärgad väst', 'Signalfärgad mössa', 'Jaktradio'],
    contactName: opts.me?.name || opts.userName || '',
    contactPhone: opts.me?.phone || '',
    sendEmail: true,
  };
}

/** Lokal tid (YYYY-MM-DD + HH:MM) → ISO i UTC. Tom tid ger null. */
export function toIso(date: string, time: string): string | null {
  if (!date || !/^\d{1,2}:\d{2}$/.test(time || '')) return null;
  const d = new Date(`${date}T${time.padStart(5, '0')}:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// ─── Validering per steg ──────────────────────────────────────────

export type Errors = Partial<Record<'title' | 'date' | 'meetingTime' | 'startTime' | 'endTime' | 'meetingPlace' | 'recipients', string>>;

/** meId = den planerandes medlems-id; jaktledaren är alltid med och räknas inte som mottagare. */
export function validateStep(step: StepKey, d: PlanDraft, meId?: string | null): Errors {
  const e: Errors = {};
  if (step === 'nar') {
    if (!d.title.trim()) e.title = 'Ge jakten en rubrik';
    if (!d.date) e.date = 'Välj datum';
    if (!toIso(d.date, d.meetingTime)) e.meetingTime = 'Ange samlingstid';
    if (!toIso(d.date, d.startTime)) e.startTime = 'Ange när jakten börjar';
    if (d.endTime && !toIso(d.date, d.endTime)) e.endTime = 'Skriv tiden som 15:00';
    else if (d.endTime && d.startTime && d.endTime <= d.startTime.padStart(5, '0')) e.endTime = 'Slutet måste vara efter starten';
    if (!e.meetingTime && !e.startTime && d.meetingTime.padStart(5, '0') > d.startTime.padStart(5, '0')) e.meetingTime = 'Samlingen bör vara före jaktstart';
  }
  if (step === 'plats') {
    if (!d.meetingPlace.trim() && d.meetingLat == null) e.meetingPlace = 'Välj samlingsplats på kartan eller skriv var ni ses';
  }
  if (step === 'vilka') {
    if (d.memberIds.filter(id => id !== meId).length + d.guests.length < 1) e.recipients = 'Bjud in minst en person';
  }
  return e;
}

export function validateAll(d: PlanDraft, meId?: string | null): { step: StepKey; errors: Errors } | null {
  for (const s of STEPS) {
    const errors = validateStep(s.key, d, meId);
    if (Object.keys(errors).length) return { step: s.key, errors };
  }
  return null;
}

// ─── Mottagare ────────────────────────────────────────────────────

export interface Recipient { key: string; name: string; dogHandler: boolean; isGuest: boolean; memberId?: string; email?: string; phone?: string }

export function recipientsOf(d: PlanDraft, members: PlanMember[]): Recipient[] {
  const out: Recipient[] = [];
  for (const id of d.memberIds) {
    const m = members.find(x => x.id === id);
    if (m) out.push({ key: `m:${m.id}`, name: m.name, dogHandler: !!m.dogHandler, isGuest: false, memberId: m.id, email: m.userEmail || undefined, phone: m.phone || undefined });
  }
  for (const g of d.guests) out.push({ key: g.key, name: g.name, dogHandler: false, isGuest: true, email: g.email, phone: g.phone });
  return out;
}

/** Tolkar "namn@exempel.se", "070-123 45 67" eller "Johan Berg johan@x.se". */
export function parseGuest(contact: string, name: string): Guest | { error: string } {
  const c = contact.trim();
  const email = c.match(/[^\s<>]+@[^\s<>]+\.[^\s<>]+/)?.[0]?.toLowerCase();
  const digits = c.replace(/[^\d+]/g, '');
  if (email) return { key: `g:${email}`, name: name.trim() || email, email };
  if (digits.replace(/\D/g, '').length >= 7) return { key: `g:${digits}`, name: name.trim() || c, phone: c };
  return { error: 'Skriv en e-postadress eller ett telefonnummer' };
}

// ─── Pass ─────────────────────────────────────────────────────────

export interface Post { satIdx: number; marker: PlanMarker; label: string }

/** Alla pass och hundförarposter i planen, med visningsnamn ("Pass 3 · Såt 1"). */
export function postsOf(d: PlanDraft): Post[] {
  const out: Post[] = [];
  d.satar.forEach((s, satIdx) => {
    const n = { pass: 0, hund_start: 0 };
    for (const m of s.markers) {
      if (m.type !== 'pass' && m.type !== 'hund_start') continue;
      const i = ++n[m.type];
      const name = m.label.trim() || (m.type === 'pass' ? `Pass ${i}` : `Hundförare ${i}`);
      out.push({ satIdx, marker: m, label: d.satar.length > 1 || s.namn ? `${name} · ${s.namn}` : name });
    }
  });
  return out;
}

export interface DrawResult { draft: PlanDraft; unassigned: string[]; emptyPosts: number }

/**
 * Lottar pass bland de inbjudna: hundförare till hundförarposter, övriga till pass.
 * Låsta pass (t.ex. jaktledare/hundförare) ingår inte: de behåller sin tilldelning och den
 * som står där lottas inte. Går det inte jämnt ut blir några utan pass (eller några pass tomma)
 * — det sägs i resultatet. Går att lotta om hur många gånger som helst innan inbjudan skickas.
 */
export function drawPosts(d: PlanDraft, members: PlanMember[], random: () => number = Math.random): DrawResult {
  const posts = postsOf(d);
  const lockedKeys = new Set(posts.filter(p => p.marker.locked && p.marker.assigneeKey).map(p => p.marker.assigneeKey!));
  const everyone = recipientsOf(d, members);
  const people = everyone.filter(p => !lockedKeys.has(p.key));
  const shuffle = <T,>(a: T[]) => { const r = [...a]; for (let i = r.length - 1; i > 0; i--) { const j = Math.floor(random() * (i + 1)); [r[i], r[j]] = [r[j], r[i]]; } return r; };
  const dogs = shuffle(people.filter(p => p.dogHandler));
  const hunters = shuffle(people.filter(p => !p.dogHandler));
  const open = posts.filter(p => !p.marker.locked);
  const hundPosts = open.filter(p => p.marker.type === 'hund_start');
  const passPosts = open.filter(p => p.marker.type === 'pass');
  // Hundförare som inte får en hundförarpost lottas in bland passen.
  const extraDogs = dogs.slice(hundPosts.length);
  const shooters = shuffle([...hunters, ...extraDogs]);
  const assign = new Map<string, Recipient | undefined>();
  hundPosts.forEach((p, i) => assign.set(p.marker.id, dogs[i]));
  passPosts.forEach((p, i) => assign.set(p.marker.id, shooters[i]));
  const satar = d.satar.map(s => ({
    ...s,
    markers: s.markers.map(m => {
      if (!assign.has(m.id)) return m;
      const r = assign.get(m.id);
      return { ...m, assigneeKey: r?.key ?? null, assignedName: r?.name };
    }),
  }));
  const assignedKeys = new Set([...lockedKeys, ...[...assign.values()].filter(Boolean).map(r => r!.key)]);
  return {
    draft: { ...d, satar },
    unassigned: everyone.filter(p => !assignedKeys.has(p.key)).map(p => p.name),
    emptyPosts: [...assign.values()].filter(r => !r).length,
  };
}

/** Låser eller låser upp ett pass för lottningen. */
export function setPostLocked(d: PlanDraft, markerId: string, locked: boolean): PlanDraft {
  return { ...d, satar: d.satar.map(s => ({ ...s, markers: s.markers.map(m => m.id === markerId ? { ...m, locked: locked || undefined } : m) })) };
}

// ─── Markens såter (S1) ───────────────────────────────────────────

export interface MarkSatChoice { id: string; name: string; contains: (lat: number, lng: number) => boolean }
export interface MarkPassPin { id?: string; lat: number; lng: number; type: string; name?: string }

/**
 * Väljer eller väljer bort en av markens såter. Vald såt blir en såt i planen med markens pass
 * inom såten utlagda (namn från nålen). Bortval tar bort planens såt med dess pass.
 */
export function toggleMarkSat(d: PlanDraft, sat: MarkSatChoice, pins: MarkPassPin[]): PlanDraft {
  if (d.satar.some(s => s.markSatId === sat.id)) return { ...d, satar: d.satar.filter(s => s.markSatId !== sat.id) };
  const markers: PlanMarker[] = pins
    .filter(p => (p.type || '').toLowerCase() === 'pass' && Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)) && sat.contains(Number(p.lat), Number(p.lng)))
    .map(p => ({ id: uid(), type: 'pass', lat: Number(p.lat), lng: Number(p.lng), label: p.name ?? '' }));
  // En tom egen såt (t.ex. "Såt 1" utan pass) ersätts av den valda.
  const satar = d.satar.filter(s => s.markSatId || s.markers.length > 0);
  return { ...d, satar: [...satar, { id: uid(), namn: sat.name, markSatId: sat.id, markers }] };
}

/** Namnen på markens såter som planen gäller, för inbjudan. */
export function markSatNames(d: Pick<PlanDraft, 'satar'>): string[] {
  return d.satar.filter(s => s.markSatId).map(s => s.namn);
}

export function assignPost(d: PlanDraft, markerId: string, key: string | null, members: PlanMember[]): PlanDraft {
  const r = key ? recipientsOf(d, members).find(x => x.key === key) : undefined;
  return {
    ...d,
    satar: d.satar.map(s => ({
      ...s,
      markers: s.markers.map(m => {
        // En person kan bara ha ett pass: flytta i stället för att dubblera.
        if (m.id !== markerId) return key && m.assigneeKey === key ? { ...m, assigneeKey: null, assignedName: undefined } : m;
        return { ...m, assigneeKey: r?.key ?? null, assignedName: r?.name };
      }),
    })),
  };
}

export function passLabelFor(d: PlanDraft, key: string): string | null {
  const p = postsOf(d).find(x => x.marker.assigneeKey === key);
  if (!p) return null;
  return p.marker.type === 'hund_start' && !p.label.startsWith('Hundförare') ? `Hundförare · ${p.label}` : p.label;
}

/** Tar bort tilldelningar till personer som inte längre är inbjudna. */
export function pruneAssignments(d: PlanDraft, members: PlanMember[]): PlanDraft {
  const keys = new Set(recipientsOf(d, members).map(r => r.key));
  let changed = false;
  const satar = d.satar.map(s => ({
    ...s,
    markers: s.markers.map(m => {
      if (m.assigneeKey && !keys.has(m.assigneeKey)) { changed = true; return { ...m, assigneeKey: null, assignedName: undefined }; }
      return m;
    }),
  }));
  return changed ? { ...d, satar } : d;
}

// ─── Till och från API:t ──────────────────────────────────────────

export function toPlanHuntInput(d: PlanDraft, members: PlanMember[]): PlanHuntInput {
  const recipients = recipientsOf(d, members).map(r => ({
    memberId: r.memberId ?? null,
    email: r.memberId ? null : r.email ?? null,
    phone: r.memberId ? null : r.phone ?? null,
    name: r.name,
    passLabel: passLabelFor(d, r.key),
  }));
  return {
    title: d.title.trim(),
    startTime: toIso(d.date, d.startTime)!,
    endTime: d.endTime ? toIso(d.date, d.endTime) : null,
    meetingTime: toIso(d.date, d.meetingTime)!,
    meetingPlace: d.meetingPlace.trim() || null,
    meetingLat: d.meetingLat,
    meetingLng: d.meetingLng,
    message: d.message.trim() || null,
    contactName: d.contactName.trim() || null,
    contactPhone: d.contactPhone.trim() || null,
    satPlanJson: JSON.stringify({ version: 2, satar: d.satar, utrustning: d.utrustning }),
    recipients,
  };
}

/** Bygger ett utkast av en sparad jakt (för "Ändra"). Äldre planer (utan version) läses så gott det går. */
export function draftFromEvent(ev: MarkCalendarEvent, members: PlanMember[]): PlanDraft {
  const start = new Date(ev.startTime);
  const plan = (() => { try { return ev.satPlanJson ? JSON.parse(ev.satPlanJson) : null; } catch { return null; } })();
  const satar: PlanSat[] = Array.isArray(plan?.satar) ? plan.satar.map((s: any) => ({
    id: String(s.id ?? uid()), namn: String(s.namn ?? 'Såt'), startTid: s.startTid, slutTid: s.slutTid, markSatId: typeof s.markSatId === 'string' ? s.markSatId : null,
    markers: (s.markers ?? []).map((m: any) => ({ id: String(m.id ?? uid()), type: m.type in MARKER_LABEL ? m.type : 'pass', lat: Number(m.lat), lng: Number(m.lng), label: m.label ?? '', assigneeKey: m.assigneeKey ?? null, assignedName: m.assignedName, ...(m.locked ? { locked: true } : {}) })),
  })) : [];
  const memberIds: string[] = [];
  const guests: Guest[] = [];
  for (const inv of ev.invites as CalendarEventInvite[]) {
    const m = (inv.memberId && members.find(x => x.id === inv.memberId)) || (inv.email && members.find(x => x.userEmail?.toLowerCase() === inv.email!.toLowerCase()));
    if (m) { if (!memberIds.includes(m.id)) memberIds.push(m.id); continue; }
    const contact = inv.email || inv.phone;
    if (contact) guests.push({ key: `g:${inv.email ? inv.email.toLowerCase() : String(inv.phone).replace(/[^\d+]/g, '')}`, name: inv.name, email: inv.email || undefined, phone: inv.phone || undefined });
  }
  return {
    version: 2,
    title: ev.title,
    date: isoDate(start),
    meetingTime: ev.meetingTime ? hhmm(new Date(ev.meetingTime)) : hhmm(start),
    startTime: hhmm(start),
    endTime: ev.endTime ? hhmm(new Date(ev.endTime)) : '',
    meetingPlace: ev.meetingPlace || ev.location || '',
    meetingLat: ev.meetingLat ?? null,
    meetingLng: ev.meetingLng ?? null,
    satar,
    memberIds,
    guests,
    message: ev.message || plan?.anteckningar || '',
    utrustning: Array.isArray(plan?.utrustning) ? plan.utrustning : [],
    contactName: ev.contactName || '',
    contactPhone: ev.contactPhone || '',
    sendEmail: true,
  };
}

/** En mall (ny eller gammal SatPlanerare-mall) läggs över utkastet: såtar, utrustning och meddelande. */
export function applyTemplate(d: PlanDraft, planJson: string): PlanDraft {
  const p = JSON.parse(planJson);
  return {
    ...d,
    satar: Array.isArray(p.satar) ? p.satar.map((s: any) => ({ ...s, id: uid(), markers: (s.markers ?? []).map((m: any) => ({ ...m, id: uid(), assigneeKey: null, assignedName: undefined, assignedInitials: undefined, locked: undefined })) })) : d.satar,
    utrustning: Array.isArray(p.utrustning) ? p.utrustning : d.utrustning,
    message: p.message || p.anteckningar || d.message,
  };
}

export function templateJson(d: PlanDraft): string {
  return JSON.stringify({ version: 2, satar: d.satar.map(s => ({ ...s, markers: s.markers.map(m => ({ ...m, assigneeKey: null, assignedName: undefined, locked: undefined })) })), utrustning: d.utrustning, message: d.message });
}

// ─── Utkast i localStorage ────────────────────────────────────────

const draftKey = (markId: string) => `qvenari_planera_${markId}`;

export function loadDraft(markId: string): PlanDraft | null {
  try {
    const raw = localStorage.getItem(draftKey(markId));
    const d = raw ? JSON.parse(raw) : null;
    return d?.version === 2 ? d : null;
  } catch { return null; }
}

export function saveDraft(markId: string, d: PlanDraft): void {
  try { localStorage.setItem(draftKey(markId), JSON.stringify(d)); } catch { /* privat läge */ }
}

export function clearDraft(markId: string): void {
  try { localStorage.removeItem(draftKey(markId)); } catch { /* */ }
}
